// Google Drive バックアップユーティリティ
// Google Identity Services (GIS) の Token Client で OAuth、drive.file スコープで
// 本アプリが作成したファイルのみアクセスできる。
// CLAUDE.md S1-1: クライアント ID は秘密ではないが localStorage に保存。
// access_token はメモリ + localStorage (1時間有効、自動再取得)。

const GIS_SRC = 'https://accounts.google.com/gsi/client';
const SCOPE = 'https://www.googleapis.com/auth/drive.file';
const FOLDER_NAME = 'lectio backup';

const LS_CLIENT_ID = 'lectio.drive.clientId';
const LS_TOKEN = 'lectio.drive.token';
const LS_TOKEN_EXP = 'lectio.drive.tokenExp';
const LS_LAST_BACKUP = 'lectio.drive.lastBackup';
const LS_AUTO_BACKUP = 'lectio.drive.autoBackup';
const LS_FOLDER_ID = 'lectio.drive.folderId';

let tokenClient = null;
let gsiLoaded = false;

export const drive = {
  getClientId: () => localStorage.getItem(LS_CLIENT_ID) || '',
  setClientId: (id) => localStorage.setItem(LS_CLIENT_ID, id || ''),
  getLastBackup: () => parseInt(localStorage.getItem(LS_LAST_BACKUP) || '0', 10),
  setLastBackup: (ts) => localStorage.setItem(LS_LAST_BACKUP, String(ts)),
  isAutoBackup: () => localStorage.getItem(LS_AUTO_BACKUP) === '1',
  setAutoBackup: (on) => localStorage.setItem(LS_AUTO_BACKUP, on ? '1' : '0'),
  getToken,
  signOut,
};

function getToken() {
  const t = localStorage.getItem(LS_TOKEN);
  const exp = parseInt(localStorage.getItem(LS_TOKEN_EXP) || '0', 10);
  if (t && exp > Date.now() + 60_000) return t;
  return null;
}

function setToken(token, expiresIn) {
  localStorage.setItem(LS_TOKEN, token);
  localStorage.setItem(LS_TOKEN_EXP, String(Date.now() + (expiresIn || 3600) * 1000));
}

function signOut() {
  localStorage.removeItem(LS_TOKEN);
  localStorage.removeItem(LS_TOKEN_EXP);
  localStorage.removeItem(LS_FOLDER_ID);
}

// GIS スクリプトを動的に読み込み
async function loadGsi() {
  if (gsiLoaded && window.google?.accounts?.oauth2) return;
  await new Promise((resolve, reject) => {
    if (document.querySelector(`script[src="${GIS_SRC}"]`)) {
      const check = () => {
        if (window.google?.accounts?.oauth2) resolve();
        else setTimeout(check, 50);
      };
      check();
      return;
    }
    const s = document.createElement('script');
    s.src = GIS_SRC;
    s.async = true;
    s.defer = true;
    s.onload = () => resolve();
    s.onerror = () => reject(new Error('GIS load failed'));
    document.head.appendChild(s);
  });
  gsiLoaded = true;
}

// OAuth トークンを取得（同意画面が必要なら開く）
export async function ensureToken({ interactive = true } = {}) {
  const clientId = drive.getClientId();
  if (!clientId) throw new Error('Google OAuth クライアント ID が未設定です');
  const cached = getToken();
  if (cached) return cached;
  await loadGsi();
  return new Promise((resolve, reject) => {
    tokenClient = window.google.accounts.oauth2.initTokenClient({
      client_id: clientId,
      scope: SCOPE,
      prompt: interactive ? '' : 'none',
      callback: (resp) => {
        if (resp.error) {
          reject(new Error(resp.error));
          return;
        }
        setToken(resp.access_token, resp.expires_in);
        resolve(resp.access_token);
      },
      error_callback: (err) => reject(new Error(err.type || 'OAuth error')),
    });
    tokenClient.requestAccessToken();
  });
}

async function driveFetch(path, init = {}, token) {
  const res = await fetch(`https://www.googleapis.com${path}`, {
    ...init,
    headers: { Authorization: `Bearer ${token}`, ...(init.headers || {}) },
  });
  if (res.status === 401) {
    signOut();
    throw new Error('Drive 認証が切れました。再接続してください');
  }
  if (!res.ok) {
    const text = await res.text().catch(() => '');
    throw new Error(`Drive API ${res.status}: ${text.slice(0, 200)}`);
  }
  return res.json();
}

// バックアップ用フォルダを取得または作成
async function ensureBackupFolder(token) {
  const cached = localStorage.getItem(LS_FOLDER_ID);
  if (cached) {
    // 削除されている可能性は低いが、明示確認はせずキャッシュを信用
    return cached;
  }
  // drive.file スコープでは本アプリが作成したファイルのみ可視
  const search = await driveFetch(
    `/drive/v3/files?q=${encodeURIComponent(`name='${FOLDER_NAME}' and mimeType='application/vnd.google-apps.folder' and trashed=false`)}&fields=files(id,name)&pageSize=1`,
    {},
    token
  );
  if (search.files?.length) {
    localStorage.setItem(LS_FOLDER_ID, search.files[0].id);
    return search.files[0].id;
  }
  // 作成
  const created = await driveFetch('/drive/v3/files', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ name: FOLDER_NAME, mimeType: 'application/vnd.google-apps.folder' }),
  }, token);
  localStorage.setItem(LS_FOLDER_ID, created.id);
  return created.id;
}

// 全データを 1 つの JSON にダンプ
async function buildSnapshot() {
  const db = await new Promise((res, rej) => {
    const r = indexedDB.open('lectio');
    r.onsuccess = () => res(r.result);
    r.onerror = () => rej(r.error);
  });
  const stores = ['books', 'journals', 'aiCache', 'wishlist', 'aiHistory', 'embeddings'];
  const snapshot = { version: 1, createdAt: new Date().toISOString(), stores: {}, localStorage: {} };
  for (const name of stores) {
    if (!db.objectStoreNames.contains(name)) {
      snapshot.stores[name] = [];
      continue;
    }
    const tx = db.transaction(name, 'readonly');
    const req = tx.objectStore(name).getAll();
    snapshot.stores[name] = await new Promise((res, rej) => {
      req.onsuccess = () => res(req.result || []);
      req.onerror = () => rej(req.error);
    });
  }
  // localStorage の lectio.* を保存（OAuth トークン関連は除外）
  for (const k of Object.keys(localStorage)) {
    if (!k.startsWith('lectio.')) continue;
    if (k === LS_TOKEN || k === LS_TOKEN_EXP) continue;
    snapshot.localStorage[k] = localStorage.getItem(k);
  }
  return snapshot;
}

// バックアップを Drive にアップロード
export async function uploadBackup(progressCb) {
  if (progressCb) progressCb('認証確認中…');
  const token = await ensureToken({ interactive: true });
  if (progressCb) progressCb('スナップショット作成中…');
  const snapshot = await buildSnapshot();
  const jsonStr = JSON.stringify(snapshot);
  // Blob 圧縮: gzip があれば使う
  let body, mediaType;
  if ('CompressionStream' in window) {
    if (progressCb) progressCb('圧縮中…');
    const stream = new Blob([jsonStr]).stream().pipeThrough(new CompressionStream('gzip'));
    body = await new Response(stream).blob();
    mediaType = 'application/gzip';
  } else {
    body = new Blob([jsonStr], { type: 'application/json' });
    mediaType = 'application/json';
  }

  if (progressCb) progressCb('バックアップフォルダ確認中…');
  const folderId = await ensureBackupFolder(token);

  const ts = new Date();
  const stamp = `${ts.getFullYear()}-${pad(ts.getMonth() + 1)}-${pad(ts.getDate())}-${pad(ts.getHours())}${pad(ts.getMinutes())}${pad(ts.getSeconds())}`;
  const filename = `lectio-backup-${stamp}.json${mediaType === 'application/gzip' ? '.gz' : ''}`;

  if (progressCb) progressCb('Drive へアップロード中…');
  // Multipart upload
  const metadata = { name: filename, parents: [folderId], mimeType: mediaType };
  const boundary = '----lectio-boundary-' + Math.random().toString(36).slice(2);
  const head =
    `--${boundary}\r\n` +
    `Content-Type: application/json; charset=UTF-8\r\n\r\n` +
    JSON.stringify(metadata) + `\r\n` +
    `--${boundary}\r\n` +
    `Content-Type: ${mediaType}\r\n\r\n`;
  const tail = `\r\n--${boundary}--`;
  const parts = new Blob([head, body, tail]);
  const uploaded = await fetch(
    `https://www.googleapis.com/upload/drive/v3/files?uploadType=multipart&fields=id,name,size,modifiedTime`,
    {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': `multipart/related; boundary=${boundary}`,
      },
      body: parts,
    }
  );
  if (!uploaded.ok) {
    const text = await uploaded.text().catch(() => '');
    throw new Error(`Drive upload ${uploaded.status}: ${text.slice(0, 200)}`);
  }
  const result = await uploaded.json();
  drive.setLastBackup(Date.now());

  // 古いバックアップを 10 件超ならローテーション
  try {
    await rotateBackups(token, folderId, 10);
  } catch (e) {
    console.warn('rotate skipped:', e?.message);
  }

  if (progressCb) progressCb('完了');
  return result;
}

async function rotateBackups(token, folderId, keep) {
  const list = await driveFetch(
    `/drive/v3/files?q=${encodeURIComponent(`'${folderId}' in parents and trashed=false`)}&fields=files(id,name,createdTime)&orderBy=createdTime desc&pageSize=50`,
    {},
    token
  );
  const files = list.files || [];
  if (files.length <= keep) return;
  const toDelete = files.slice(keep);
  await Promise.all(
    toDelete.map((f) =>
      fetch(`https://www.googleapis.com/drive/v3/files/${f.id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      })
    )
  );
}

// Drive 接続確認（同意画面なしでトークンが取れるか）
export async function tryAuthSilent() {
  try {
    await ensureToken({ interactive: false });
    return true;
  } catch {
    return false;
  }
}

// 起動時の自動バックアップチェック（30日経過ならバックアップ）
const AUTO_BACKUP_INTERVAL = 30 * 24 * 60 * 60 * 1000;
export async function maybeAutoBackup(progressCb) {
  if (!drive.isAutoBackup()) return null;
  if (!drive.getClientId()) return null;
  const last = drive.getLastBackup();
  if (last && Date.now() - last < AUTO_BACKUP_INTERVAL) return null;
  try {
    // silent でトークンが取れる場合のみ自動実行
    const ok = await tryAuthSilent();
    if (!ok) return null;
    return await uploadBackup(progressCb);
  } catch (e) {
    console.warn('auto backup failed:', e?.message);
    return null;
  }
}

function pad(n) {
  return String(n).padStart(2, '0');
}
